import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, RotateCcw, Trash2, Pencil, MessageSquare, X, ThumbsUp, ThumbsDown } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ago = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
};

const Composer = ({ onSubmit, placeholder, autoFocus, onCancel, testid, initial = "", members = [], allowEmpty }) => {
  const [text, setText] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState(null);
  const [picked, setPicked] = useState({});
  const go = async () => {
    setBusy(true);
    const mentions = Object.entries(picked).filter(([, n]) => text.includes(`@${n}`)).map(([id]) => id);
    try { await onSubmit(text.trim(), mentions); setText(""); setPicked({}); } finally { setBusy(false); }
  };
  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const m = v.slice(0, e.target.selectionStart).match(/@([^\s@]{0,30})$/);
    setQ(m ? m[1].toLowerCase() : null);
  };
  const choose = (m) => {
    const n = m.name || m.email;
    const i = text.lastIndexOf(`@${q}`);
    setText(i === -1 ? `${text}@${n} ` : `${text.slice(0, i)}@${n} ${text.slice(i + q.length + 1)}`);
    setPicked({ ...picked, [m.user_id]: n });
    setQ(null);
  };
  const opts = q === null ? [] : members.filter((m) => `${m.name || ""} ${m.email}`.toLowerCase().includes(q)).slice(0, 6);
  return (
    <div className="relative space-y-2">
      <Textarea rows={2} value={text} onChange={onChange} placeholder={placeholder} autoFocus={autoFocus} className="text-sm" data-testid={`${testid}-input`}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && (text.trim() || allowEmpty)) go(); }} />
      {opts.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-md border border-[#2D3442] bg-[#0F131A] p-1 shadow-xl" data-testid={`${testid}-mentions`}>
          {opts.map((m) => (
            <button key={m.user_id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(m)} data-testid={`${testid}-mention-${m.user_id}`}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-[#E8DFD1] hover:bg-[#1A202C]">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#D97706]/25 text-[10px] font-bold text-[#F59E0B]">{(m.name || m.email)[0].toUpperCase()}</span>
              {m.name || m.email}<span className="ml-auto text-[10px] capitalize text-[#A8A090]">{m.role}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex justify-end gap-2">
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`${testid}-cancel`}>Cancel</Button>}
        <Button size="sm" onClick={go} disabled={busy || (!text.trim() && !allowEmpty)} data-testid={`${testid}-submit`}>{busy ? "Posting…" : "Post"}</Button>
      </div>
    </div>
  );
};

const Body = ({ text, members, testid }) => {
  const names = members.map((m) => m.name || m.email).filter(Boolean).sort((a, b) => b.length - a.length);
  if (!names.length || !text) return <p className="mt-1 whitespace-pre-wrap text-sm text-[#E8DFD1]" data-testid={testid}>{text}</p>;
  const rx = new RegExp(`(@(?:${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}))`, "g");
  return <p className="mt-1 whitespace-pre-wrap text-sm text-[#E8DFD1]" data-testid={testid}>{text.split(rx).map((part, i) => (i % 2 ? <span key={i} className="rounded bg-[#38BDF8]/15 px-0.5 text-[#7DD3FC]">{part}</span> : part))}</p>;
};

const SuggestionBox = ({ c, ctx, run }) => {
  const { user } = useAuth();
  const votes = Object.entries(c.votes || {});
  const up = votes.filter(([, v]) => v.v > 0), down = votes.filter(([, v]) => v.v < 0);
  const mine = c.votes?.[user?.id]?.v || 0;
  const decide = async (decision) => {
    if (decision === "accepted" && !ctx.applyRef.current?.(c)) { toast.error("The passage changed, so this suggestion can't be applied automatically."); return; }
    await run(() => api.post(`/comments/${c.id}/decide`, { decision }), decision === "accepted" ? "Suggestion accepted" : "Suggestion rejected");
  };
  const mayDecide = ctx.canDecide && (c.by_id !== user?.id || ctx.isOwner);
  return (
    <div className="mb-2 space-y-1.5 text-sm" data-testid={`suggestion-${c.id}`}>
      {c.quote && <p className="rounded bg-red-500/10 px-2 py-1 text-red-300 line-through decoration-red-400">{c.quote}</p>}
      {c.replacement ? <p className="rounded bg-[#10B981]/15 px-2 py-1 text-[#6EE7B7]">{c.replacement}</p> : <p className="text-xs italic text-[#A8A090]">Suggests deleting this.</p>}
      {c.status !== "open" && <p className={`text-[10px] uppercase tracking-wider ${c.status === "accepted" ? "text-[#10B981]" : "text-[#F87171]"}`} data-testid={`suggestion-status-${c.id}`}>{c.status} by {c.decided_by}</p>}
      {c.status === "open" && (
        <div className="flex flex-wrap items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {ctx.allowVotes && (
            <>
              <Button size="sm" variant={mine > 0 ? "default" : "outline"} className="h-7 px-2 text-xs" disabled={!ctx.canEdit} title={up.map(([, v]) => v.name).join(", ")}
                onClick={() => run(() => api.post(`/comments/${c.id}/vote`, { vote: mine > 0 ? 0 : 1 }))} data-testid={`suggestion-upvote-${c.id}`}><ThumbsUp className="mr-1 h-3 w-3" />{up.length}</Button>
              <Button size="sm" variant={mine < 0 ? "default" : "outline"} className="h-7 px-2 text-xs" disabled={!ctx.canEdit} title={down.map(([, v]) => v.name).join(", ")}
                onClick={() => run(() => api.post(`/comments/${c.id}/vote`, { vote: mine < 0 ? 0 : -1 }))} data-testid={`suggestion-downvote-${c.id}`}><ThumbsDown className="mr-1 h-3 w-3" />{down.length}</Button>
            </>
          )}
          {mayDecide && (
            <>
              <Button size="sm" className="h-7 bg-[#10B981] px-2 text-xs text-[#0B0D10] hover:bg-[#34D399]" onClick={() => decide("accepted")} data-testid={`suggestion-accept-${c.id}`}><Check className="mr-1 h-3 w-3" />Accept</Button>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-300" onClick={() => decide("rejected")} data-testid={`suggestion-reject-${c.id}`}><X className="mr-1 h-3 w-3" />Reject</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Thread = ({ c, active, onPick, canEdit, isOwner, refresh, found, ctx }) => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const run = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div onClick={() => onPick(c.id)} data-testid={`comment-${c.id}`}
      className={`cursor-pointer rounded-md border p-3 transition-colors ${active ? "border-[#D97706]/70 bg-[#D97706]/5" : "border-[#2D3442] bg-[#0F131A] hover:border-[#D97706]/40"} ${c.resolved ? "opacity-70" : ""}`}>
      {c.kind === "suggestion" ? <div className="mb-1 lf-label text-[#6EE7B7]">Suggested edit</div>
        : <p className={`mb-2 border-l-2 pl-2 text-xs italic line-clamp-2 ${found ? "border-[#F59E0B]/70 text-[#D6CCBC]" : "border-[#2D3442] text-[#A8A090] line-through"}`}>“{c.quote}”</p>}
      {c.kind === "suggestion" && <SuggestionBox c={c} ctx={ctx} run={run} />}
      {!found && !c.resolved && <p className="mb-2 text-[10px] text-[#F87171]" data-testid={`comment-orphan-${c.id}`}>The passage has changed or was removed.</p>}
      <div className="flex items-baseline justify-between gap-2"><span className="text-xs font-medium text-[#F3EBDD]">{c.by_name}</span><span className="text-[10px] text-[#A8A090]">{ago(c.created_at)}</span></div>
      {editing ? (
        <div onClick={(e) => e.stopPropagation()}><Composer initial={c.body} testid={`comment-edit-${c.id}`} onCancel={() => setEditing(false)}
          onSubmit={(body) => run(() => api.patch(`/comments/${c.id}`, { body }).then(() => setEditing(false)))} /></div>
      ) : c.body ? <Body text={c.body} members={ctx.members} testid={`comment-body-${c.id}`} /> : null}
      {c.replies.map((r) => (
        <div key={r.id} className="mt-2 border-t border-[#2D3442] pt-2" data-testid={`comment-reply-${r.id}`}>
          <div className="flex items-baseline justify-between gap-2"><span className="text-xs font-medium text-[#F3EBDD]">{r.by_name}</span><span className="text-[10px] text-[#A8A090]">{ago(r.created_at)}</span></div>
          <Body text={r.body} members={ctx.members} />
        </div>
      ))}
      {c.resolved && <p className="mt-2 text-[10px] uppercase tracking-wider text-[#10B981]">Resolved by {c.resolved_by}</p>}
      {canEdit && active && (
        <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
          {!c.resolved && <Composer placeholder="Reply… (type @ to mention)" members={ctx.members} testid={`comment-reply-${c.id}`} onSubmit={(body, mentions) => run(() => api.post(`/comments/${c.id}/replies`, { body, mentions }))} />}
          <div className="flex flex-wrap gap-1">
            {c.kind !== "suggestion" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => run(() => api.patch(`/comments/${c.id}`, { resolved: !c.resolved }), c.resolved ? "Comment reopened" : "Comment resolved")} data-testid={`comment-resolve-${c.id}`}>
              {c.resolved ? <><RotateCcw className="mr-1 h-3 w-3" />Reopen</> : <><Check className="mr-1 h-3 w-3" />Resolve</>}
            </Button>}
            {c.by_id === user?.id && !editing && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(true)} data-testid={`comment-edit-button-${c.id}`}><Pencil className="mr-1 h-3 w-3" />Edit</Button>}
            {(c.by_id === user?.id || isOwner) && <Button size="sm" variant="ghost" className="h-7 text-xs text-red-300" onClick={() => run(() => api.delete(`/comments/${c.id}`), "Comment deleted")} data-testid={`comment-delete-${c.id}`}><Trash2 className="mr-1 h-3 w-3" />Delete</Button>}
          </div>
        </div>
      )}
    </div>
  );
};

export const CommentsPanel = ({ ent, comments, pending, setPending, active, onPick, canEdit, isOwner, refresh, foundIds, onClose, applyRef }) => {
  const [showResolved, setShowResolved] = useState(false);
  const { user } = useAuth();
  const qc = useQueryClient();
  const uni = qc.getQueryData(["universe", ent.universe_id]) || {};
  const pol = uni.suggestion_policy || {};
  const { data: members = [] } = useQuery({ queryKey: ["members", ent.universe_id], queryFn: () => api.get(`/universes/${ent.universe_id}/members`).then((r) => r.data) });
  const ctx = { members, applyRef, canEdit, isOwner, allowVotes: !!pol.allow_votes, canDecide: isOwner || (pol.delegates || []).includes(user?.id) };
  const [replacement, setReplacement] = useState("");
  useEffect(() => { api.post("/notifications/read", { entity_id: ent.id }).catch(() => {}); }, [ent.id, comments.length]);
  const open = comments.filter((c) => !c.resolved), done = comments.filter((c) => c.resolved);
  const list = showResolved ? done : open;
  return (
    <aside className="space-y-3" data-testid="comments-panel">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-[#F59E0B]" />
        <span className="lf-label flex-1">Comments</span>
        <button onClick={() => setShowResolved(false)} className={`text-xs ${!showResolved ? "text-[#F59E0B]" : "text-[#A8A090]"}`} data-testid="comments-tab-open">Open {open.length}</button>
        <button onClick={() => setShowResolved(true)} className={`text-xs ${showResolved ? "text-[#F59E0B]" : "text-[#A8A090]"}`} data-testid="comments-tab-resolved">Resolved {done.length}</button>
        {onClose && <button onClick={onClose} aria-label="Close comments" data-testid="comments-close" className="text-[#A8A090]"><X className="h-4 w-4" /></button>}
      </div>
      {pending && (
        <div className="rounded-md border border-[#D97706]/60 bg-[#13171F] p-3" data-testid="comment-new">
          {pending.quote ? <p className="mb-2 border-l-2 border-[#F59E0B] pl-2 text-xs italic text-[#D6CCBC] line-clamp-3">“{pending.quote}”</p>
            : <p className="mb-2 text-xs text-[#A8A090]">Insert new text after “…{pending.prefix.slice(-30)}”</p>}
          {pending.kind === "suggestion" && (
            <Textarea rows={2} value={replacement} onChange={(e) => setReplacement(e.target.value)} autoFocus className="mb-2 border-[#10B981]/50 text-sm"
              placeholder={pending.quote ? "Replace with… (leave empty to suggest deleting)" : "Text to insert"} data-testid="suggestion-new-replacement" />
          )}
          <Composer autoFocus={pending.kind !== "suggestion"} allowEmpty={pending.kind === "suggestion"} members={members} testid="comment-new" onCancel={() => { setPending(null); setReplacement(""); }}
            placeholder={pending.kind === "suggestion" ? "Why? (optional, @ to mention)" : "Add a comment… (type @ to mention)"}
            onSubmit={async (body, mentions) => {
              try {
                const { data } = await api.post(`/entities/${ent.id}/comments`, { ...pending, body, mentions, ...(pending.kind === "suggestion" ? { replacement } : {}) });
                setPending(null); setReplacement(""); refresh(); onPick(data.id);
              } catch (e) { toast.error(errMsg(e)); }
            }} />
        </div>
      )}
      {list.length === 0 && !pending && (
        <p className="text-xs text-[#A8A090]" data-testid="comments-empty">{showResolved ? "No resolved comments." : canEdit ? "Select text in the notes and tap Comment to start a discussion." : "No open comments."}</p>
      )}
      {list.map((c) => <Thread key={c.id} c={c} active={active === c.id} onPick={onPick} canEdit={canEdit} isOwner={isOwner} refresh={refresh} found={foundIds.has(c.id)} ctx={ctx} />)}
    </aside>
  );
};
